import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Add, ExpandMore, PictureAsPdf, UploadFile } from "@mui/icons-material";
import AdminAPI from "../../../../apis/AdminAPI";
import { Loader } from "../../../../components/loader/Loader";

const defaultCatalog = {
  label: "",
  image_url: "",
  pdf_url: "",
  sort_order: 0,
  enabled: true,
};

function isEnabled(catalog) {
  return (
    catalog.enabled === true ||
    catalog.enabled === 1 ||
    catalog.enabled === "1"
  );
}

function extractFileName(url) {
  if (!url) return "";
  const clean = url.split("?")[0];
  const parts = clean.split("/");
  return parts[parts.length - 1] || "";
}

function getUploadErrorMessage(error, type) {
  if (!error || typeof error !== "object") return "Echec de l'import media";
  if (error.error === "image_expected") return "Le fichier doit etre une image.";
  if (error.error === "pdf_expected") return "Le fichier doit etre un PDF.";
  if (error.error === "missing_file") return "Aucun fichier recu.";
  if (error.error === "invalid_file") return "Le fichier importe est invalide.";
  if (error.error === "upload_failed") {
    return type === "image"
      ? "Echec de l'upload de l'image."
      : "Echec de l'upload du PDF.";
  }
  return "Echec de l'import media";
}

function MediaUploadButton({ label, accept, loading, onSelect }) {
  return (
    <Button
      component="label"
      variant="outlined"
      fullWidth
      startIcon={<UploadFile />}
      disabled={loading}
    >
      {loading ? "Import en cours..." : label}
      <input
        hidden
        type="file"
        accept={accept}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onSelect(file);
          e.target.value = "";
        }}
      />
    </Button>
  );
}

function CatalogPreview({ imageUrl, pdfUrl }) {
  return (
    <Grid container spacing={2}>
      <Grid item md={6} xs={12}>
        <Paper
          variant="outlined"
          style={{
            minHeight: 120,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 8,
          }}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="Apercu"
              style={{ width: "100%", maxHeight: 220, objectFit: "contain" }}
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              Aucune image
            </Typography>
          )}
        </Paper>
        {imageUrl && (
          <Typography variant="caption" color="textSecondary">
            {extractFileName(imageUrl)}
          </Typography>
        )}
      </Grid>
      <Grid item md={6} xs={12}>
        <Paper
          variant="outlined"
          style={{
            minHeight: 120,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 8,
          }}
        >
          {pdfUrl ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<PictureAsPdf />}
              href={pdfUrl}
              target="_blank"
              rel="noreferrer"
            >
              Ouvrir le PDF
            </Button>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Aucun PDF
            </Typography>
          )}
        </Paper>
        {pdfUrl && (
          <Typography variant="caption" color="textSecondary">
            {extractFileName(pdfUrl)}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
}

export function HomeCatalogsPage() {
  const [loading, setLoading] = useState(true);
  const [catalogs, setCatalogs] = useState([]);
  const [newCatalog, setNewCatalog] = useState(defaultCatalog);
  const [addOpen, setAddOpen] = useState(false);
  const [newCatalogUploading, setNewCatalogUploading] = useState({
    image: false,
    pdf: false,
  });
  const [catalogUploading, setCatalogUploading] = useState({});

  useEffect(() => {
    loadCatalogs();
  }, []);

  async function loadCatalogs() {
    setLoading(true);
    try {
      const rows = await AdminAPI.getHomeCatalogs();
      setCatalogs(rows);
    } finally {
      setLoading(false);
    }
  }

  function updateCatalogField(id, field, value) {
    setCatalogs((prev) =>
      prev.map((cat) => (cat.id === id ? { ...cat, [field]: value } : cat))
    );
  }

  function notify(severity, message) {
    global.emitter.emit("showSnackBar", { severity, message });
  }

  function setCatalogUploadState(catalogId, type, value) {
    setCatalogUploading((prev) => ({
      ...prev,
      [catalogId]: { ...(prev[catalogId] || {}), [type]: value },
    }));
  }

  async function uploadMedia(file, type, onSuccess) {
    try {
      const res = await AdminAPI.uploadHomeCatalogMedia(file, type);
      if (!res?.url) throw new Error("upload_failed");
      onSuccess(res.url);
      notify("success", type === "image" ? "Image importee" : "PDF importe");
    } catch (e) {
      notify("error", getUploadErrorMessage(e, type));
    }
  }

  async function uploadNewCatalogMedia(type, file) {
    setNewCatalogUploading((prev) => ({ ...prev, [type]: true }));
    await uploadMedia(file, type, (url) => {
      setNewCatalog((prev) => ({
        ...prev,
        [type === "image" ? "image_url" : "pdf_url"]: url,
      }));
    });
    setNewCatalogUploading((prev) => ({ ...prev, [type]: false }));
  }

  async function uploadExistingCatalogMedia(catalogId, type, file) {
    setCatalogUploadState(catalogId, type, true);
    await uploadMedia(file, type, (url) => {
      updateCatalogField(catalogId, type === "image" ? "image_url" : "pdf_url", url);
    });
    setCatalogUploadState(catalogId, type, false);
  }

  async function createCatalog() {
    if (!newCatalog.label.trim() || !newCatalog.image_url || !newCatalog.pdf_url) {
      notify("error", "Nom, image et PDF sont requis");
      return;
    }

    await AdminAPI.createHomeCatalog({
      ...newCatalog,
      sort_order: +newCatalog.sort_order,
      enabled: !!newCatalog.enabled,
    });
    setNewCatalog(defaultCatalog);
    setAddOpen(false);
    notify("success", "Catalogue ajoute");
    await loadCatalogs();
  }

  async function saveCatalog(catalog) {
    if (!catalog.label?.trim() || !catalog.image_url || !catalog.pdf_url) {
      notify("error", "Nom, image et PDF sont requis");
      return;
    }

    await AdminAPI.updateHomeCatalog(catalog.id, {
      label: catalog.label,
      image_url: catalog.image_url,
      pdf_url: catalog.pdf_url,
      sort_order: +catalog.sort_order,
      enabled: isEnabled(catalog),
    });
    notify("success", "Catalogue mis a jour");
    await loadCatalogs();
  }

  async function removeCatalog(catalogId) {
    if (!window.confirm("Supprimer ce catalogue ?")) return;
    await AdminAPI.deleteHomeCatalog(catalogId);
    notify("success", "Catalogue supprime");
    await loadCatalogs();
  }

  return (
    <div>
      {loading && <Loader height={320} />}
      {!loading && (
        <Paper style={{ width: "100%", padding: 12 }}>
          <Alert severity="info">
            Ce module pilote la section "Nos catalogues" de la page d'accueil.
          </Alert>
          <br />

          <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
            <Grid item>
              <Typography variant="h5">Ajouter un catalogue</Typography>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setAddOpen((prev) => !prev)}
              >
                {addOpen ? "Masquer" : "Nouveau"}
              </Button>
            </Grid>
          </Grid>
          <Collapse in={addOpen}>
            <br />
            <Grid container spacing={2}>
              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nom du catalogue"
                  value={newCatalog.label}
                  onChange={(e) =>
                    setNewCatalog({ ...newCatalog, label: e.target.value })
                  }
                />
              </Grid>
              <Grid item md={2} xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Ordre"
                  value={newCatalog.sort_order}
                  onChange={(e) =>
                    setNewCatalog({ ...newCatalog, sort_order: e.target.value })
                  }
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newCatalog.enabled}
                      onChange={(e) =>
                        setNewCatalog({ ...newCatalog, enabled: e.target.checked })
                      }
                    />
                  }
                  label="Actif"
                />
              </Grid>
            </Grid>
            <br />
            <Grid container spacing={2}>
              <Grid item md={6} xs={12}>
                <MediaUploadButton
                  label="Importer image"
                  accept="image/*"
                  loading={newCatalogUploading.image}
                  onSelect={(file) => uploadNewCatalogMedia("image", file)}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <MediaUploadButton
                  label="Importer PDF"
                  accept="application/pdf"
                  loading={newCatalogUploading.pdf}
                  onSelect={(file) => uploadNewCatalogMedia("pdf", file)}
                />
              </Grid>
            </Grid>
            <br />
            <CatalogPreview
              imageUrl={newCatalog.image_url}
              pdfUrl={newCatalog.pdf_url}
            />
            <br />
            <Button
              variant="contained"
              color="success"
              onClick={createCatalog}
              disabled={
                !newCatalog.image_url ||
                !newCatalog.pdf_url ||
                newCatalogUploading.image ||
                newCatalogUploading.pdf
              }
            >
              Ajouter
            </Button>
          </Collapse>

          <br />
          <Typography variant="h5">Catalogues existants</Typography>
          <br />
          {catalogs.map((catalog) => (
            <Accordion key={catalog.id} disableGutters style={{ marginBottom: 8 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs>
                    <Typography style={{ fontWeight: 600 }}>
                      {catalog.label || `Catalogue #${catalog.id}`}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Chip
                      size="small"
                      label={isEnabled(catalog) ? "Actif" : "Inactif"}
                      color={isEnabled(catalog) ? "success" : "default"}
                    />
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item md={6} xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Nom du catalogue"
                      value={catalog.label || ""}
                      onChange={(e) =>
                        updateCatalogField(catalog.id, "label", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item md={2} xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Ordre"
                      value={catalog.sort_order}
                      onChange={(e) =>
                        updateCatalogField(catalog.id, "sort_order", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item md={4} xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isEnabled(catalog)}
                          onChange={(e) =>
                            updateCatalogField(catalog.id, "enabled", e.target.checked)
                          }
                        />
                      }
                      label="Actif"
                    />
                  </Grid>
                </Grid>
                <br />
                <Grid container spacing={2}>
                  <Grid item md={6} xs={12}>
                    <MediaUploadButton
                      label="Remplacer image"
                      accept="image/*"
                      loading={!!catalogUploading[catalog.id]?.image}
                      onSelect={(file) =>
                        uploadExistingCatalogMedia(catalog.id, "image", file)
                      }
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <MediaUploadButton
                      label="Remplacer PDF"
                      accept="application/pdf"
                      loading={!!catalogUploading[catalog.id]?.pdf}
                      onSelect={(file) =>
                        uploadExistingCatalogMedia(catalog.id, "pdf", file)
                      }
                    />
                  </Grid>
                </Grid>
                <br />
                <CatalogPreview
                  imageUrl={catalog.image_url}
                  pdfUrl={catalog.pdf_url}
                />
                <br />
                <Grid container spacing={2}>
                  <Grid item md={2} xs={12}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      onClick={() => saveCatalog(catalog)}
                      disabled={!catalog.image_url || !catalog.pdf_url}
                    >
                      Sauvegarder
                    </Button>
                  </Grid>
                  <Grid item md={2} xs={12}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="error"
                      onClick={() => removeCatalog(catalog.id)}
                    >
                      Supprimer
                    </Button>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      )}
    </div>
  );
}
